import { EmptyState, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@atlas/ui-components'
import { Badge } from '@atlas/ui-primitives'
import { formatUtc } from '@/lib/format-utc'
import type { WorkfrontStreamDTO } from '@/services/workfront.service'
import { formatDuration } from './StatusBadges'

/** Beyond this, a stream is behind and the adapter raises `STREAM_LAG`. */
export const STREAM_LAG_LIMIT_SECONDS = 3 * 60 * 60

export interface StreamsTableProps {
	streams: WorkfrontStreamDTO[]
}

/** One discovery stream: where it is, whether a run holds it, and how far behind it is. */
function StreamRow({ stream }: { stream: WorkfrontStreamDTO }) {
	const behind = stream.lagSeconds !== null && stream.lagSeconds > STREAM_LAG_LIMIT_SECONDS
	return (
		<TableRow>
			<TableCell className="font-mono">{stream.streamCode}</TableCell>
			<TableCell>
				{stream.cursorAt ? (
					<span title={stream.cursorDate ?? undefined}>{formatUtc(stream.cursorAt)}</span>
				) : (
					<span className="text-ds-text-muted">not read yet</span>
				)}
			</TableCell>
			<TableCell>
				{stream.lastSuccessAt ? formatUtc(stream.lastSuccessAt) : <span className="text-ds-text-muted">never</span>}
			</TableCell>
			<TableCell>
				{stream.lagSeconds === null ? (
					<span className="text-ds-text-muted">—</span>
				) : (
					<Badge size="sm" variant={behind ? 'warning' : 'muted'}>
						{formatDuration(stream.lagSeconds)}
					</Badge>
				)}
			</TableCell>
			<TableCell>
				{stream.leaseOwner ? (
					<Badge size="sm" variant="secondary">
						reading
					</Badge>
				) : null}
			</TableCell>
		</TableRow>
	)
}

/**
 * The discovery streams. Lag is the time since a stream last read successfully,
 * not its cursor's age: a stream with nothing new has an old cursor and is not
 * behind.
 */
export function StreamsTable({ streams }: StreamsTableProps) {
	if (streams.length === 0) return <EmptyState title="No streams yet" description="Discovery has not run." />
	return (
		<Table aria-label="Discovery streams">
			<TableHeader>
				<TableRow>
					<TableHead>Stream</TableHead>
					<TableHead>Cursor</TableHead>
					<TableHead>Last read</TableHead>
					<TableHead>Lag</TableHead>
					<TableHead>Lease</TableHead>
				</TableRow>
			</TableHeader>
			<TableBody>
				{streams.map((stream) => (
					<StreamRow key={stream.streamCode} stream={stream} />
				))}
			</TableBody>
		</Table>
	)
}
